import { platformRoleAccess } from "helpers/RolesPermission";
import {
  filterSuperAdminNavigationSections,
  flattenSuperAdminNavigation,
} from "Constants/Navigations/superAdminNavigation.utils";
import { SuperAdminNavigationSections } from "Constants/Navigations/SuperAdminNavigations";

export const DASHBOARD_SECTIONS = {
  TENANTS: "tenants",
  PROSPECTS: "prospects",
  BILLING_ALERTS: "billing_alerts",
  AUDIT_ACTIVITY: "audit_activity",
  USAGE: "usage",
  AI_USAGE: "ai_usage",
  CATALOG: "catalog",
  OPERATIONS: "operations",
};

export const DASHBOARD_PROFILES = {
  OWNER: "owner",
  SUPPORT: "support",
  CUSTOMER_SUCCESS: "customer_success",
  BILLING: "billing",
  OPERATIONS: "operations",
  AUDITOR: "auditor",
  CATALOG: "catalog",
  GENERAL: "general",
};

/**
 * Resolve platform-admin capabilities from login payload permissions.
 */
export const resolvePlatformDashboardCapabilities = () => ({
  canViewDashboard: platformRoleAccess("platform.dashboard.view"),
  canListTenants: platformRoleAccess("platform.tenant.list"),
  canViewTenants: platformRoleAccess("platform.tenant.view"),
  canViewProspects: platformRoleAccess("platform.prospect.list"),
  canViewBilling: platformRoleAccess("platform.billing.view"),
  canManageBilling: platformRoleAccess("platform.billing.manage"),
  canManagePlans: platformRoleAccess("platform.plans.manage"),
  canViewAudit: platformRoleAccess("platform.audit.view"),
  canExportAudit: platformRoleAccess("platform.audit.export"),
  canViewUsage: platformRoleAccess("platform.usage.view"),
  canViewAiUsage: platformRoleAccess("platform.ai_usage.view"),
  canSearchUsers: platformRoleAccess("platform.user.search"),
  canUpdateEmail: platformRoleAccess("platform.user.email_update"),
  canViewStaff: platformRoleAccess("platform.staff.list"),
  canManageRoles: platformRoleAccess("platform.roles.manage"),
  canStartGhost: platformRoleAccess("platform.ghost.start"),
  canViewGhostSessions: platformRoleAccess("platform.ghost.end"),
  canViewCatalog: platformRoleAccess("platform.catalog.forms"),
  canViewIntegrations: platformRoleAccess("platform.integrations.view"),
  canSuspendTenants: platformRoleAccess("platform.tenant.suspend"),
});

export const resolveDashboardProfile = (capabilities = {}, primaryRoles = []) => {
  const roleName = primaryRoles[0] || "";

  if (roleName.includes("Owner")) {
    return DASHBOARD_PROFILES.OWNER;
  }
  if (roleName.includes("Support")) {
    return DASHBOARD_PROFILES.SUPPORT;
  }
  if (roleName.includes("Customer Success")) {
    return DASHBOARD_PROFILES.CUSTOMER_SUCCESS;
  }
  if (roleName.includes("Billing")) {
    return DASHBOARD_PROFILES.BILLING;
  }
  if (roleName.includes("Operations")) {
    return DASHBOARD_PROFILES.OPERATIONS;
  }
  if (roleName.includes("Auditor")) {
    return DASHBOARD_PROFILES.AUDITOR;
  }
  if (roleName.includes("Catalog")) {
    return DASHBOARD_PROFILES.CATALOG;
  }

  if (capabilities.canViewCatalog && !capabilities.canListTenants) {
    return DASHBOARD_PROFILES.CATALOG;
  }

  if (capabilities.canSearchUsers && !capabilities.canViewBilling) {
    return DASHBOARD_PROFILES.SUPPORT;
  }

  if (capabilities.canViewBilling && !capabilities.canSearchUsers) {
    return DASHBOARD_PROFILES.BILLING;
  }

  return DASHBOARD_PROFILES.GENERAL;
};

export const getDashboardProfileMeta = (profile) => {
  switch (profile) {
    case DASHBOARD_PROFILES.SUPPORT:
      return {
        title: "Support workspace",
        description: "Tenant lookup, user account fixes, and audit trail.",
      };
    case DASHBOARD_PROFILES.CUSTOMER_SUCCESS:
      return {
        title: "Customer success overview",
        description: "Tenants, prospects, billing health, and adoption.",
      };
    case DASHBOARD_PROFILES.BILLING:
      return {
        title: "Billing & commercial overview",
        description: "Subscriptions, revenue signals, and prospect pipeline.",
      };
    case DASHBOARD_PROFILES.OPERATIONS:
      return {
        title: "Platform operations",
        description: "Tenant health, suspensions, integrations, and usage.",
      };
    case DASHBOARD_PROFILES.AUDITOR:
      return {
        title: "Governance overview",
        description: "Read-only tenant and audit visibility.",
      };
    case DASHBOARD_PROFILES.CATALOG:
      return {
        title: "Catalog workspace",
        description: "Master forms and platform catalog assets.",
      };
    case DASHBOARD_PROFILES.OWNER:
      return {
        title: "Platform overview",
        description: "Full visibility across tenants, billing, and governance.",
      };
    default:
      return {
        title: "Platform overview",
        description: "Metrics and actions scoped to your role.",
      };
  }
};

export const hasVisibleSection = (visibleSections = [], section) =>
  Array.isArray(visibleSections) && visibleSections.includes(section);

/** Capability-based sections so widgets can render before stats returns. */
export const visibleSectionsFromCapabilities = (capabilities = {}) => {
  const sections = [];

  if (capabilities.canListTenants) {
    sections.push(DASHBOARD_SECTIONS.TENANTS);
  }
  if (capabilities.canViewProspects) {
    sections.push(DASHBOARD_SECTIONS.PROSPECTS);
  }
  if (capabilities.canViewBilling) {
    sections.push(DASHBOARD_SECTIONS.BILLING_ALERTS);
  }
  if (capabilities.canViewAudit) {
    sections.push(DASHBOARD_SECTIONS.AUDIT_ACTIVITY);
  }
  if (capabilities.canViewUsage) {
    sections.push(DASHBOARD_SECTIONS.USAGE);
  }
  if (capabilities.canViewAiUsage) {
    sections.push(DASHBOARD_SECTIONS.AI_USAGE);
  }
  if (capabilities.canViewCatalog) {
    sections.push(DASHBOARD_SECTIONS.CATALOG);
  }
  if (capabilities.canSuspendTenants) {
    sections.push(DASHBOARD_SECTIONS.OPERATIONS);
  }

  return sections;
};

export const shouldFetchDashboardStats = (capabilities = {}) =>
  Boolean(
    capabilities.canViewDashboard &&
      (capabilities.canListTenants ||
        capabilities.canViewAudit ||
        capabilities.canViewBilling ||
        capabilities.canViewProspects ||
        capabilities.canViewCatalog ||
        capabilities.canSuspendTenants ||
        capabilities.canViewUsage ||
        capabilities.canViewAiUsage)
  );

export const shouldFetchBillingOverview = (capabilities = {}) =>
  Boolean(capabilities.canViewDashboard && capabilities.canViewBilling);

export const getPlatformRoleLabel = (user, primaryRoles = []) => {
  if (primaryRoles.length > 0) {
    return primaryRoles[0];
  }

  const roles = user?.platform_roles;
  if (!Array.isArray(roles) || roles.length === 0) {
    return "Platform staff";
  }

  const owner = roles.find((role) => role.is_platform_owner);
  if (owner?.name) {
    return owner.name;
  }

  return roles[0]?.name || "Platform staff";
};

export const getDashboardWorkspaceSubtitle = (profileMeta, computedAt) => {
  if (computedAt) {
    return `${profileMeta.description} · Updated ${computedAt}`;
  }

  return profileMeta.description;
};

export const getAccessiblePlatformNavItems = () =>
  flattenSuperAdminNavigation(SuperAdminNavigationSections);

export const hasAnyPlatformAdminAccess = () =>
  getAccessiblePlatformNavItems().length > 0;

export const buildDashboardQuickActions = (capabilities = {}) => {
  const actions = [
    {
      id: "tenants",
      label: "All tenants",
      description: "Browse and manage service providers",
      to: "/tenants",
      show: capabilities.canListTenants,
    },
    {
      id: "users",
      label: "Search users",
      description: "Account support and login fixes",
      to: "/users",
      show: capabilities.canSearchUsers,
    },
    {
      id: "prospects",
      label: "Prospects",
      description: "Sales leads and invitations",
      to: "/prospects",
      show: capabilities.canViewProspects,
    },
    {
      id: "billing",
      label: "Financial overview",
      description: "Subscriptions, revenue, and follow-up",
      to: "/billing",
      show: capabilities.canViewBilling,
    },
    {
      id: "stripe",
      label: "Stripe management",
      description: "Customers, plans, and invoice sync",
      to: "/stripe",
      show: capabilities.canViewBilling || capabilities.canManagePlans,
    },
    {
      id: "audit",
      label: "Audit log",
      description: "Platform actions and compliance trail",
      to: "/audit-log",
      show: capabilities.canViewAudit,
    },
    {
      id: "ghost",
      label: "Ghost sessions",
      description: "Active and recent impersonation sessions",
      to: "/ghost-sessions",
      show: capabilities.canViewGhostSessions,
    },
    {
      id: "usage",
      label: "Feature usage",
      description: "Module adoption across tenants",
      to: "/usage",
      show: capabilities.canViewUsage,
    },
    {
      id: "ai_usage",
      label: "AI usage",
      description: "Cross-tenant AI credit spend",
      to: "/ai-usage",
      show: capabilities.canViewAiUsage,
    },
    {
      id: "forms_crm",
      label: "Forms — CRM",
      description: "Master CRM form templates",
      to: "/forms-list",
      show: capabilities.canViewCatalog,
    },
    {
      id: "forms_hrm",
      label: "Forms — HRM",
      description: "Master HRM form templates",
      to: "/forms-list-hrm",
      show: capabilities.canViewCatalog,
    },
    {
      id: "staff",
      label: "Platform staff",
      description: "Manage platform admin users",
      to: "/staff-users",
      show: capabilities.canViewStaff,
    },
    {
      id: "roles",
      label: "Platform roles",
      description: "Roles and permission assignments",
      to: "/platform-roles",
      show: capabilities.canManageRoles,
    },
  ];

  return actions.filter((action) => action.show);
};

const HEADER_QUICK_ACTION_PRIORITY = {
  [DASHBOARD_PROFILES.SUPPORT]: ["users", "ghost", "audit", "tenants"],
  [DASHBOARD_PROFILES.CATALOG]: ["forms_crm", "forms_hrm"],
  [DASHBOARD_PROFILES.BILLING]: ["billing", "stripe", "prospects", "tenants"],
  [DASHBOARD_PROFILES.CUSTOMER_SUCCESS]: ["tenants", "prospects", "billing", "ai_usage", "usage"],
  [DASHBOARD_PROFILES.OPERATIONS]: ["tenants", "ai_usage", "usage", "audit"],
  [DASHBOARD_PROFILES.AUDITOR]: ["audit", "tenants", "usage"],
  [DASHBOARD_PROFILES.OWNER]: ["tenants", "users", "billing", "audit"],
};

/** Profile-prioritized shortcuts for the dashboard header (max 6). */
export const buildDashboardHeaderQuickActions = (
  capabilities = {},
  profile = DASHBOARD_PROFILES.GENERAL
) => {
  const actions = buildDashboardQuickActions(capabilities);
  const priority = HEADER_QUICK_ACTION_PRIORITY[profile] || [];
  const byId = Object.fromEntries(actions.map((action) => [action.id, action]));
  const ordered = priority.map((id) => byId[id]).filter(Boolean);
  const remaining = actions.filter((action) => !priority.includes(action.id));

  return [...ordered, ...remaining].slice(0, 6);
};

export const getDashboardPanelVisibility = (capabilities = {}, visibleSections = []) => {
  const hasSection = (section) => hasVisibleSection(visibleSections, section);

  return {
    showNeedsAttention:
      capabilities.canViewDashboard &&
      hasSection(DASHBOARD_SECTIONS.BILLING_ALERTS) &&
      capabilities.canListTenants,
    showOperationsAttention:
      capabilities.canViewDashboard &&
      hasSection(DASHBOARD_SECTIONS.OPERATIONS) &&
      !hasSection(DASHBOARD_SECTIONS.BILLING_ALERTS),
    showRecentSignups:
      capabilities.canViewDashboard && hasSection(DASHBOARD_SECTIONS.TENANTS),
    showRecentActivity:
      capabilities.canViewDashboard && hasSection(DASHBOARD_SECTIONS.AUDIT_ACTIVITY),
    showUsageSummary:
      capabilities.canViewDashboard && hasSection(DASHBOARD_SECTIONS.USAGE),
    showAiUsageSummary:
      capabilities.canViewDashboard && hasSection(DASHBOARD_SECTIONS.AI_USAGE),
    showCatalogSummary:
      capabilities.canViewDashboard && hasSection(DASHBOARD_SECTIONS.CATALOG),
    showQuickActions: capabilities.canViewDashboard,
    showPlatformMetrics:
      capabilities.canViewDashboard &&
      (hasSection(DASHBOARD_SECTIONS.TENANTS) ||
        hasSection(DASHBOARD_SECTIONS.PROSPECTS)),
    showFinancialMetrics:
      capabilities.canViewDashboard && capabilities.canViewBilling,
  };
};

export const filterNavigationSectionsForUser = () =>
  filterSuperAdminNavigationSections(SuperAdminNavigationSections);

export const buildSuspendedAttentionItems = (suspendedTenants = []) =>
  suspendedTenants.map((tenant) => ({
    id: `suspended-${tenant.id}`,
    tenantId: tenant.id,
    label: `Suspended — ${tenant.name}`,
    detail: tenant.email || null,
    borderClass: "border-red-400",
  }));

export const buildCatalogMetricChips = (catalog = {}) => [
  {
    id: "crm_forms",
    label: "CRM forms",
    value: String(catalog.crm_forms ?? "—"),
    to: "/forms-list",
    title: "Master CRM form templates",
  },
  {
    id: "hrm_forms",
    label: "HRM forms",
    value: String(catalog.hrm_forms ?? "—"),
    to: "/forms-list-hrm",
    title: "Master HRM form templates",
  },
  {
    id: "total_forms",
    label: "Total forms",
    value: String(catalog.total_forms ?? "—"),
    to: "/forms-list",
    title: "Combined catalog form count",
  },
];

export const buildUsageMetricChips = (usage = {}) => [
  {
    id: "tenants_tracked",
    label: "Tenants tracked",
    value: String(usage.tenants_tracked ?? "—"),
    to: "/usage",
    title: "Tenants with usage snapshots",
  },
];
